import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { finalize } from 'rxjs/operators';
import { KeyManagerProvider, KeyManagerService, Keys, KeyManagerStatus } from './key-manager.service';
import { ApiService } from '../../../core/services/api.service';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { DropdownComponent, DropdownOption } from '../../../shared/components/ui/dropdown/dropdown.component';
import { IconComponentV2 } from '../../../shared/components/icon-component/icon.component';
import { SlidePanelComponent } from '../../../shared/components/slide-panel/slide-panel.component';
import { AuditLogPanelComponent } from '../../../shared/components/audit-log-panel/audit-log-panel.component';
import { AuditEntityType } from '../../../core/models/audit-log.model';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';
import { LoaderService } from '../../../core/services/loader.service';
import { copyToClipboard } from '../../../core/services/clipboard';



import { ToasterService } from '../../../core/services/toaster.service';
import { toast } from 'ngx-sonner';
import { MatDialog } from '@angular/material/dialog';
import {
    ConfirmationModalComponent,
    ConfirmationModalData,
} from '../../../shared/components/confirmation-modal/confirmation-modal.component';

/** State for the secure-access URL dialog (token from regenerate; URL built client-side). */
interface AccessUrlDisplay {
    accessUrl: string;
    ttlSeconds?: number | null;
    expiresAt?: Date | null;
    singleUse?: boolean | null;
}

type StatusFilterValue = 'ALL' | KeyManagerStatus;

/** Which async action is running for a key in the details panel (per-button loading text). */
type PanelKeyAction = 'copyKey' | 'copyUrl' | 'regenerate' | 'rotate' | 'revoke';

interface StatCard {
    label: string;
    value: number;
    /** Lucide icon name (PascalCase) for {@link IconComponentV2}. */
    icon: string;
}

@Component({
    selector: 'app-key-manager',
    standalone: true,
    imports: [
        CommonModule,
        ButtonComponent,
        DropdownComponent,
        IconComponentV2,
        SlidePanelComponent,
        DatePipe,
        AuditLogPanelComponent,
        PaginationComponent,
    ],

    templateUrl: './key-manager.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './key-manager.component.scss',
})
export class KeyManagerComponent implements OnInit, OnDestroy {
    @ViewChild(AuditLogPanelComponent) auditLogPanel!: AuditLogPanelComponent;
    private readonly keyManager = inject(KeyManagerService);
    private readonly toaster = inject(ToasterService);
    private readonly dialog = inject(MatDialog);
    private readonly apiService = inject(ApiService);
    private readonly loader = inject(LoaderService);


    readonly keys = signal<Keys[]>([]);
    readonly isListLoading = signal(false);
    readonly listError = signal<string | null>(null);

    readonly searchQuery = signal('');
    readonly statusFilter = signal<StatusFilterValue>(KeyManagerStatus.ACTIVE);
    readonly providerFilter = signal<string>('ALL');

    readonly products = signal<any[]>([]);
    readonly auditEntityTypes = [AuditEntityType.KEY_MANAGER];

    readonly sidebarProductPid = computed(() => {
        const id = this.apiService.selectedProductId() || sessionStorage.getItem('selected_product_id');
        const products = this.apiService.products();
        const product = products.find((p) => p.id === id);
        return product?.pid || null;
    });

    readonly detailsOpen = signal(false);
    readonly selectedKey = signal<Keys | null>(null);

    readonly accessDialogOpen = signal(false);
    readonly accessUrlResult = signal<AccessUrlDisplay | null>(null);
    readonly accessUrlForRow = signal<Keys | null>(null);
    readonly accessUrlGeneratedAt = signal<Date | null>(null);
    readonly revealFullUrl = signal(false);
    readonly remainingLabel = signal<string | null>(null);

    /** In-flight action for the open details panel (copy / regenerate / rotate / revoke). */
    readonly panelKeyAction = signal<{ pid: number; action: PanelKeyAction } | null>(null);
    readonly accessBusy = signal(false);
    /** Draft new API key for rotate flow (cleared after success or when panel closes). */
    readonly rotateNewApiKey = signal('');
    readonly rotateApiKeyTouched = signal(false);
    readonly rotateApiKeyVisible = signal(false);

    readonly createDialogOpen = signal(false);
    readonly createBusy = signal(false);

    readonly allowAccessDialogOpen = signal(false);
    readonly allowAccessSearch = signal('');
    readonly allowAccessNote = signal('');
    readonly allowAccessSelected = signal<Set<number>>(new Set());
    readonly allowAccessTouched = signal<Set<number>>(new Set());
    readonly allowAccessUsers = signal<any[]>([]);

    readonly allowAccessItems = computed(() => {
        return this.allowAccessUsers().map((u) => {
            const first = u.firstName || '';
            const last = u.lastName || '';
            const name = `${first} ${last}`.trim();
            return {
                label: name || u.userName || u.email || 'Unknown User',
                userId: u.pid,
            };
        });
    });

    readonly allowAccessFilteredItems = computed(() => {
        const items = this.allowAccessItems();
        const q = this.allowAccessSearch().trim().toLowerCase();
        if (!q) return items;
        return items.filter((i) => i.label.toLowerCase().includes(q));
    });

    readonly createName = signal('');
    readonly createPurpose = signal('');
    readonly createApiKey = signal('');
    readonly createProvider = signal<KeyManagerProvider>(KeyManagerProvider.OPENAI);
    readonly createNameTouched = signal(false);
    readonly createPurposeTouched = signal(false);
    readonly createApiKeyTouched = signal(false);
    readonly createApiKeyVisible = signal(false);

    readonly rotateApiKeyHasSpaces = computed(() => /\s/.test(this.rotateNewApiKey()));

    readonly currentPage = signal(1);
    readonly pageSize = signal(10);
    readonly totalItems = signal(0);

    readonly isCreateFormValid = computed(() => {
        const name = this.createName().trim();
        const purpose = this.createPurpose().trim();
        const apiKey = this.createApiKey();
        return (
            name.length > 0 &&
            name.length < 255 &&
            purpose.length > 0 &&
            apiKey.length >= 8 &&
            !/\s/.test(apiKey)
        );
    });

    readonly createApiKeyHasSpaces = computed(() => /\s/.test(this.createApiKey()));

    readonly createProviderOptions: DropdownOption[] = [
        { value: KeyManagerProvider.OPENAI, label: 'OpenAI' },
        { value: KeyManagerProvider.ANTHROPIC, label: 'Anthropic' },
        { value: KeyManagerProvider.GOOGLE_AI, label: 'Google AI' },
        { value: KeyManagerProvider.AZURE_OPENAI, label: 'Azure OpenAI' },
        { value: KeyManagerProvider.MISTRAL, label: 'Mistral' },
        { value: KeyManagerProvider.COHERE, label: 'Cohere' },
        { value: KeyManagerProvider.CUSTOM, label: 'Custom' },
    ];

    readonly statusOptions: DropdownOption[] = [
        { value: 'ALL', label: 'All statuses' },
        { value: KeyManagerStatus.ACTIVE, label: 'Active' },
        { value: KeyManagerStatus.REVOKED, label: 'Revoked' },
    ];

    readonly providerOptions = computed<DropdownOption[]>(() => {
        const set = new Set<string>();
        for (const k of this.keys()) {
            set.add(k.provider);
        }
        const opts: DropdownOption[] = [{ value: 'ALL', label: 'All providers' }];
        for (const p of [...set].sort()) {
            opts.push({ value: p, label: this.formatProvider(p) });
        }
        return opts;
    });





    constructor() {
        effect(() => {
            if (this.sidebarProductPid()) {
                this.loadKeys();
                this.loadAllowAccessUsers();
            }
        });
    }

    private loadAllowAccessUsers(): void {
        const pid = this.sidebarProductPid();
        if (!pid) return;

        this.keyManager.getUsersByProduct(pid).subscribe({
            next: (res: any) => {
                // The API returns { users: [...] } inside data
                let list: any[] = [];
                if (Array.isArray(res)) {
                    list = res;
                } else if (res && typeof res === 'object' && Array.isArray(res.users)) {
                    list = res.users;
                } else if (res && typeof res === 'object' && Array.isArray(res.data)) {
                    list = res.data;
                }

                this.allowAccessUsers.set(list);
            },
            error: () => {
                // Silently fail or log as this is secondary to main key management
                console.warn('Failed to load users for Allow Access selection');
            },
        });
    }

    readonly stats = computed(() => {
        const list = this.keys();
        const total = list.length;
        const active = list.filter((k) => k.status === KeyManagerStatus.ACTIVE).length;
        const expired = list.filter((k) => k.status === KeyManagerStatus.EXPIRED).length;
        const revoked = list.filter((k) => k.status === KeyManagerStatus.REVOKED).length;
        return { total, active, expired, revoked };
    });

    readonly statCards = computed<StatCard[]>(() => {
        const s = this.stats();
        return [
            { label: 'Total keys', value: s.total, icon: 'Lock' },
            { label: 'Active', value: s.active, icon: 'CircleCheckBig' },
            // { label: 'Expired', value: s.expired, icon: 'Hourglass' },
            { label: 'Revoked', value: s.revoked, icon: 'Shield' },
        ];

    });

    readonly filteredKeys = computed(() => {
        const q = this.searchQuery().trim().toLowerCase();
        const st = this.statusFilter();
        const prov = this.providerFilter();

        return this.keys().filter((k) => {
            if (st !== 'ALL' && k.status !== st) return false;
            if (prov !== 'ALL' && k.provider !== prov) return false;
            if (q) {
                const creator = k.createdByUserName ?? '';
                const hay =
                    `${k.name} ${k.purpose} ${k.displayPrefix} ${k.id} ${creator}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    });

    readonly hasActiveFilters = computed(() => {
        return (
            this.searchQuery().trim().length > 0 ||
            this.statusFilter() !== 'ALL' ||
            this.providerFilter() !== 'ALL'
        );
    });

    private countdownId: ReturnType<typeof setInterval> | null = null;

    ngOnInit(): void {
        this.fetchProducts();
        this.loadKeys();
    }

    fetchProducts(): void {
        this.keyManager.getProducts().subscribe({
            next: (data) => this.products.set(data),
            error: () => this.toaster.error('Failed to load products', 'Key Manager'),
        });
    }

    ngOnDestroy(): void {
        this.clearCountdown();
    }

    loadKeys(): void {
        this.isListLoading.set(true);
        this.listError.set(null);
        const st = this.statusFilter();
        const status = st === 'ALL' ? undefined : st;
        const productId = this.sidebarProductPid() || undefined;
        const page = this.currentPage();
        const limit = this.pageSize();

        this.loader.show({ message: 'Loading keys...' });
        this.keyManager
            .getKeys({ status, productId, page, limit })
            .pipe(finalize(() => {
                this.isListLoading.set(false);
                this.loader.hide();
            }))
            .subscribe({

                next: (rows: any) => {
                    const keys = this.normalizeKeyList(rows) || [];
                    this.keys.set(keys);

                    // Extract total items for pagination
                    // ApiService unwraps 'data' automatically if 'success' is present
                    const pagination = rows?.pagination;
                    if (pagination?.total != null) {
                        this.totalItems.set(pagination.total);
                    } else {
                        // Fallback in case raw response or different nesting
                        const total = rows?.total ?? rows?.data?.pagination?.total ?? rows?.data?.total;
                        if (total != null) this.totalItems.set(total);
                    }

                    const sel = this.selectedKey();
                    if (sel) {
                        const u = this.keys().find((k) => k.pid === sel.pid);
                        if (u) this.selectedKey.set(u);
                    }
                },
                error: (err: unknown) => {
                    const msg =
                        (err as { message?: string })?.message ??
                        'Unable to load keys. Check your connection and permissions.';
                    this.listError.set(msg);
                    this.toaster.error(msg, 'Key Manager');
                },
            });
    }

    onPageChange(page: number): void {
        this.currentPage.set(page);
        this.loadKeys();
    }

    onPageSizeChange(size: number): void {
        this.pageSize.set(size);
        this.currentPage.set(1);
        this.loadKeys();
    }

    onSearchInput(value: string): void {
        this.searchQuery.set(value);
        this.currentPage.set(1);
    }

    onStatusFilter(value: string | string[]): void {
        const v = Array.isArray(value) ? value[0] : value;
        const next = ((v as StatusFilterValue) ?? 'ALL') as StatusFilterValue;
        if (next === this.statusFilter()) return;
        this.statusFilter.set(next);
        this.currentPage.set(1);
        this.loadKeys();
    }

    onProviderFilter(value: string | string[]): void {
        const v = Array.isArray(value) ? value[0] : value;
        this.providerFilter.set(v ?? 'ALL');
        this.currentPage.set(1);
        this.loadKeys();
    }

    clearFilters(): void {
        this.searchQuery.set('');
        this.statusFilter.set('ALL');
        this.providerFilter.set('ALL');
        this.currentPage.set(1);
        this.loadKeys();
    }

    openCreateKeyDialog(): void {
        this.resetCreateForm();
        this.createDialogOpen.set(true);
    }



    closeCreateKeyDialog(): void {
        this.createDialogOpen.set(false);
        this.resetCreateForm();
    }

    private resetCreateForm(): void {
        this.createName.set('');
        this.createPurpose.set('');
        this.createApiKey.set('');
        this.createProvider.set(KeyManagerProvider.OPENAI);
        this.createNameTouched.set(false);
        this.createPurposeTouched.set(false);
        this.createApiKeyTouched.set(false);
        this.createApiKeyVisible.set(false);
    }

    onCreateProviderSelect(value: string | string[]): void {
        const v = Array.isArray(value) ? value[0] : value;
        if (v && (Object.values(KeyManagerProvider) as string[]).includes(v)) {
            this.createProvider.set(v as KeyManagerProvider);
        }
    }


    submitCreateKey(): void {
        this.createNameTouched.set(true);
        this.createPurposeTouched.set(true);
        this.createApiKeyTouched.set(true);

        if (!this.isCreateFormValid()) {
            //    this.toaster.error('Please fix the errors in the form before proceeding.', 'Create key');
            return;
        }

        this.createBusy.set(true);
        this.keyManager
            .createKey({
                name: this.createName().trim(),
                purpose: this.createPurpose().trim(),
                apiKey: this.createApiKey(),
                productId: this.sidebarProductPid() || this.products()?.[0]?.pid,
                provider: this.createProvider(),
            })
            .pipe(finalize(() => this.createBusy.set(false)))
            .subscribe({
                next: () => {
                    this.createDialogOpen.set(false);
                    this.resetCreateForm();
                    this.toaster.success(
                        'Key created. Open it and use Generate access URL to copy the API URL with access token.',
                        'Key Manager',
                        6000,
                    );
                    this.loadKeys();
                    this.auditLogPanel?.reloadLogs();
                },
                error: () => {
                    this.toaster.error('Failed to create key.', 'Create key');
                },
            });
    }

    openDetails(key: Keys): void {
        this.selectedKey.set(key);
        this.rotateNewApiKey.set('');
        this.rotateApiKeyTouched.set(false);
        this.rotateApiKeyVisible.set(false);
        this.detailsOpen.set(true);
    }

    closeDetails(): void {
        this.detailsOpen.set(false);
        this.rotateNewApiKey.set('');
        this.rotateApiKeyTouched.set(false);
        this.rotateApiKeyVisible.set(false);
    }

    /** Close the slide panel and clear selection (used on every sidebar action button). */
    private closeSidebar(): void {
        this.selectedKey.set(null);
        this.detailsOpen.set(false);
        this.rotateNewApiKey.set('');
    }

    canRevokeOrRotate(status: KeyManagerStatus): boolean {
        return status === KeyManagerStatus.ACTIVE || status === KeyManagerStatus.DISABLED;
    }

    private denyPrivilegedKeyManagerAction(): void {
        this.toaster.error(
            'You do not have permission for this action in the production environment.',
            'Key Manager',
        );
    }

    requestRevokeKey(key: Keys): void {
        this.closeSidebar();
        this.dialog
            .open(ConfirmationModalComponent, {
                width: '420px',
                data: {
                    title: 'Revoke this API key?',
                    description: `Systems using "${key.name}" will stop authenticating. This action cannot be undone.`,
                    confirmLabel: 'Revoke key',
                    variant: 'danger',
                } as ConfirmationModalData,
            })
            .afterClosed()
            .subscribe((confirmed: boolean) => {
                if (!confirmed) return;
                this.setPanelKeyAction(key.pid, 'revoke');
                this.keyManager
                    .revokeKey(key.pid)
                    .pipe(finalize(() => this.clearPanelKeyAction()))
                    .subscribe({
                        next: () => {
                            this.toaster.success('Key revoked.', 'Key Manager');
                            this.loadKeys();
                            this.auditLogPanel?.reloadLogs();
                        },
                        error: () => {
                            this.toaster.error('Failed to revoke key.', 'Key Manager');
                        },
                    });
            });
    }

    submitRotateKey(key: Keys): void {
        this.rotateApiKeyTouched.set(true);
        const newApiKey = this.rotateNewApiKey(); // don't trim yet to check for spaces

        if (!newApiKey) {
            this.toaster.error('Enter the new API key.', 'Rotate key');
            return;
        }

        if (this.rotateApiKeyHasSpaces()) {
            this.toaster.error('Spaces are not allowed in the API key.', 'Rotate key');
            return;
        }

        if (newApiKey.length < 8) {
            this.toaster.error('API key must be at least 8 characters long.', 'Rotate key');
            return;
        }

        this.closeSidebar();
        this.setPanelKeyAction(key.pid, 'rotate');
        this.keyManager
            .rotateKey(key.pid, newApiKey.trim())
            .pipe(finalize(() => this.clearPanelKeyAction()))
            .subscribe({
                next: () => {
                    this.toaster.success('Key rotated successfully.', 'Key Manager');
                    this.loadKeys();
                    this.auditLogPanel?.reloadLogs();
                },
                error: () => {
                    this.toaster.error('Failed to rotate key.', 'Key Manager');
                },
            });
    }

    copyDecryptedKey(key: Keys): void {
        this.closeSidebar();
        this.setPanelKeyAction(key.pid, 'copyKey');
        this.keyManager
            .originalKey(key.pid)
            .pipe(finalize(() => this.clearPanelKeyAction()))
            .subscribe({
                next: (secret) => {
                    if (!secret?.trim()) {
                        this.toaster.error('Could not read decrypted key from the response.', 'Copy key');
                        return;
                    }
                    copyToClipboard(secret).then(
                        () => {
                            toast.success('Decrypted key copied to clipboard. Clear clipboard when finished.', {
                                duration: 4000,
                            });
                            this.loadKeys();
                        },
                        () =>
                            toast.error('Clipboard is not available in this context.', {
                                duration: 4000,
                            }),
                    );
                },
                error: () => {
                    this.toaster.error('Failed to decrypt or fetch the key.', 'Copy key');
                },
            });
    }

    /**
     * Same URL source as Generate access URL: GET .../pid/:pid/access-url, then copy to clipboard.
     */
    copyAccessUrl(key: Keys): void {
        this.closeSidebar();
        this.setPanelKeyAction(key.pid, 'copyUrl');
        this.keyManager
            .buildPublicAccessUrl(key.pid)
            .pipe(finalize(() => this.clearPanelKeyAction()))
            .subscribe({
                next: (url) => {
                    const u = url?.trim();
                    if (!u) {
                        this.toaster.error('Server did not return an access URL.', 'Copy URL');
                        return;
                    }
                    copyToClipboard(u).then(
                        () => this.toaster.success('Access URL copied to clipboard.', 'Copied', 5000),
                        () => this.toaster.error('Clipboard is not available.', 'Copy URL'),
                    );
                },
                error: () => {
                    this.toaster.error(
                        'Could not load access URL. Confirm GET /key-manager/pid/:pid/access-url exists.',
                        'Copy URL',
                    );
                },
            });
    }

    /** Loads shareable URL from API and opens dialog (same source as Copy Access URL). */
    generateAccessUrl(key: Keys): void {
        this.closeSidebar();
        this.accessBusy.set(true);
        this.accessUrlForRow.set(key);
        this.revealFullUrl.set(false);
        this.keyManager
            .buildPublicAccessUrl(key.pid)
            .pipe(finalize(() => this.accessBusy.set(false)))
            .subscribe({
                next: (accessUrl) => {
                    const u = accessUrl?.trim();
                    if (!u) {
                        this.toaster.error('Server did not return an access URL.', 'Secure URL');
                        return;
                    }
                    this.loadKeys();
                    this.accessUrlResult.set({
                        accessUrl: u,
                        ttlSeconds: null,
                        expiresAt: null,
                        singleUse: null,
                    });
                    this.accessUrlGeneratedAt.set(new Date());
                    this.accessDialogOpen.set(true);
                    this.remainingLabel.set(null);
                    this.clearCountdown();
                },
                error: () => {
                    this.toaster.error(
                        'Could not load access URL. Confirm GET /key-manager/pid/:pid/access-url exists.',
                        'Secure URL',
                    );
                },
            });
    }

    /** POST regenerate token; old embedded tokens/URLs may stop working until callers use a new access URL. */
    requestRegenerateAccessToken(key: Keys): void {
        this.closeSidebar();
        this.dialog
            .open(ConfirmationModalComponent, {
                width: '440px',
                data: {
                    title: 'Regenerate access token?',
                    description:
                        'Existing access tokens in shared URLs will stop working. Update any systems using old links after you copy a new access URL.',
                    confirmLabel: 'Regenerate token',
                    variant: 'primary',
                } as ConfirmationModalData,
            })
            .afterClosed()
            .subscribe((confirmed: boolean) => {
                if (!confirmed) return;
                this.setPanelKeyAction(key.pid, 'regenerate');
                this.keyManager
                    .regenerateAccessToken(key.pid)
                    .pipe(finalize(() => this.clearPanelKeyAction()))
                    .subscribe({
                        next: () => {
                            this.toaster.success(
                                'Access token regenerated. Copy the access URL again for updated links.',
                                'Key Manager',
                                6000,
                            );
                            this.loadKeys();
                            this.auditLogPanel?.reloadLogs();
                        },
                        error: () => {
                            this.toaster.error(
                                'Failed to regenerate token. Confirm POST /key-manager/pid/:pid/regenerate-access-token exists.',
                                'Key Manager',
                            );
                        },
                    });
            });
    }

    /** Same as {@link copyAccessUrl} (row toolbar / legacy). */
    copySecureUrl(key: Keys): void {
        this.copyAccessUrl(key);
    }

    closeAccessDialog(): void {
        this.accessDialogOpen.set(false);
        this.accessUrlResult.set(null);
        this.accessUrlForRow.set(null);
        this.accessUrlGeneratedAt.set(null);
        this.revealFullUrl.set(false);
        this.clearCountdown();
        this.remainingLabel.set(null);
    }

    copyUrlFromDialog(): void {
        const r = this.accessUrlResult();
        if (!r?.accessUrl) return;
        copyToClipboard(r.accessUrl).then(
            () => this.toaster.success('URL copied to clipboard.', 'Copied'),
            () => this.toaster.error('Clipboard is not available.', 'Copy'),
        );
    }

    toggleRevealUrl(): void {
        this.revealFullUrl.update((v) => !v);
    }

    displayUrl(url: string): string {
        if (this.revealFullUrl()) return url;
        return maskUrlForDisplay(url);
    }

    statusBadgeClass(status: KeyManagerStatus): string {
        switch (status) {
            case KeyManagerStatus.ACTIVE:
                return 'km-badge km-badge--success';
            case KeyManagerStatus.DISABLED:
                return 'km-badge km-badge--muted';
            case KeyManagerStatus.EXPIRED:
            case KeyManagerStatus.REVOKED:
            case KeyManagerStatus.ROTATED_OUT:
                return 'km-badge km-badge--danger';
            default:
                return 'km-badge';
        }
    }

    formatProvider(p: string): string {
        return p.replace(/_/g, ' ');
    }

    isRowBusy(pid: number): boolean {
        return this.panelKeyAction()?.pid === pid;
    }

    panelActionIs(pid: number, action: PanelKeyAction): boolean {
        const s = this.panelKeyAction();
        return s !== null && s.pid === pid && s.action === action;
    }

    /** Disables panel actions while another runs or while the access-URL dialog is loading for this key. */
    isKeyActionLocked(key: Keys): boolean {
        return (
            this.isRowBusy(key.pid) ||
            (this.accessBusy() && this.accessUrlForRow()?.pid === key.pid)
        );
    }

    private setPanelKeyAction(pid: number, action: PanelKeyAction): void {
        this.panelKeyAction.set({ pid, action });
    }

    private clearPanelKeyAction(): void {
        this.panelKeyAction.set(null);
    }

    private normalizeKeyList(raw: any): Keys[] {
        if (!raw) return [];
        // If raw is the array itself
        if (Array.isArray(raw)) return raw;

        // If nested in data: [] or items: []
        if (raw.data && Array.isArray(raw.data)) return raw.data;
        if (raw.items && Array.isArray(raw.items)) return raw.items;

        // If nested in data: { keyManagers: [] }
        if (raw.data && typeof raw.data === 'object' && Array.isArray(raw.data.keyManagers)) {
            return raw.data.keyManagers;
        }

        // If nested in keyManagers: []
        if (Array.isArray(raw.keyManagers)) return raw.keyManagers;

        // Fallback: look for ANY array inside data object
        if (raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) {
            for (const key of Object.keys(raw.data)) {
                if (Array.isArray(raw.data[key])) return raw.data[key];
            }
        }

        // Final fallback: look for ANY array inside the root object
        if (raw && typeof raw === 'object') {
            for (const key of Object.keys(raw)) {
                if (Array.isArray(raw[key])) return raw[key];
            }
        }

        return [];
    }

    private startCountdown(expiresAt: Date | null, ttlSeconds: number | null): void {
        this.clearCountdown();
        let deadline: Date | null = expiresAt;
        if (!deadline && ttlSeconds != null && ttlSeconds > 0) {
            deadline = new Date(Date.now() + ttlSeconds * 1000);
        }
        if (!deadline) {
            this.remainingLabel.set(null);
            return;
        }
        const tick = () => {
            const ms = deadline!.getTime() - Date.now();
            if (ms <= 0) {
                this.remainingLabel.set('Expired');
                this.clearCountdown();
                return;
            }
            const m = Math.floor(ms / 60000);
            const s = Math.floor((ms % 60000) / 1000);
            this.remainingLabel.set(`${m}m ${s}s`);
        };
        tick();
        this.countdownId = setInterval(tick, 1000);
    }

    private clearCountdown(): void {
        if (this.countdownId !== null) {
            clearInterval(this.countdownId);
            this.countdownId = null;
        }
    }

    openAllowAccessDialog(): void {
        const users = this.allowAccessUsers();
        const initialSelected = new Set<number>();

        users.forEach((u) => {
            // Extract hasAccess from the nested array structure shown in the API response
            const access = u.UserKeyManagerAccessByPid?.[0]?.hasAccess;
            if (access === true) {
                initialSelected.add(u.pid);
            }
        });

        this.allowAccessSelected.set(initialSelected);
        this.allowAccessTouched.set(new Set());
        this.allowAccessNote.set('');
        this.allowAccessSearch.set('');
        this.allowAccessDialogOpen.set(true);
    }

    closeAllowAccessDialog(): void {
        this.allowAccessDialogOpen.set(false);
    }

    toggleAllowAccessItem(userId: number): void {
        // Track that this user was interacted with
        this.allowAccessTouched.update((prev) => {
            const next = new Set(prev);
            next.add(userId);
            return next;
        });

        this.allowAccessSelected.update((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });
    }

    submitAllowAccess(): void {
        const productPid = this.sidebarProductPid();
        if (!productPid) {
            this.toaster.error('No product context found for the request.', 'Error');
            return;
        }

        const selectedIds = this.allowAccessSelected();
        const touchedIds = this.allowAccessTouched();
        const allListedUsers = this.allowAccessUsers();

        // Only send users that were touched/interacted with in this session
        const keyAccess = allListedUsers
            .filter((u) => touchedIds.has(u.pid))
            .map((u) => ({
                userId: u.pid,
                hasAccess: selectedIds.has(u.pid),
            }));

        if (keyAccess.length === 0) {
            this.toaster.warning('No users available to update access for.', 'Key Manager', 3000);
            return;
        }

        this.createBusy.set(true);
        const payload = {
            productPid,
            note: this.allowAccessNote().trim(),
            keyAccess,
        };

        this.keyManager
            .bulkUpdateAccess(payload)
            .pipe(finalize(() => this.createBusy.set(false)))
            .subscribe({
                next: () => {
                    this.toaster.success('Access updated successfully for selected users.', 'Key Manager', 3000);
                    this.loadAllowAccessUsers();
                    this.closeAllowAccessDialog();
                    this.auditLogPanel?.reloadLogs();
                },

                error: (err: any) => {
                    const msg = err?.message || 'Failed to update user access bulk.';
                    this.toaster.error(msg, 'Key Manager');
                },
            });
    }
}

/** Masks token-like query values for safe shoulder-surfing. */
function maskUrlForDisplay(url: string): string {
    try {
        const u = new URL(url);
        const tokenKeys = ['token', 't', 'access_token', 'accessToken'];
        for (const key of tokenKeys) {
            const val = u.searchParams.get(key);
            if (val && val.length > 12) {
                u.searchParams.set(key, `${val.slice(0, 4)}…${val.slice(-4)}`);
            }
        }
        return u.toString();
    } catch {
        if (url.length <= 48) return url;
        return `${url.slice(0, 32)}…${url.slice(-8)}`;
    }
}
